import React from 'react'
import { Card, CardContent, CardHeader, Skeleton } from '@/shared/ui'
import { Layers } from 'lucide-react'
import { listFinanceMonthlyRevenueEstimateByPlan, type FinanceMonthlyRevenueEstimateByPlanRow } from '../services/financeReportsService'
import { formatMoneyFromCents } from '../utils/money'

type Props = {
  monthStartDate: string
  refreshKey: number
}

export function FinancePlansRevenueCards({ monthStartDate, refreshKey }: Props) {
  const [rows, setRows] = React.useState<FinanceMonthlyRevenueEstimateByPlanRow[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await listFinanceMonthlyRevenueEstimateByPlan(monthStartDate)
      setRows(data)
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar estimativa por plano')
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => { load() }, [monthStartDate, refreshKey])

  const title = 'Rentabilidade por plano (mês)'
  const subtitle = 'Ativos elegíveis (sem cancelamento) • Pago no mês + previsto até o fim do mês'

  if (loading && rows.length === 0) {
    return (
      <Card>
        <CardHeader className="space-y-1">
          <div className="text-base font-medium">{title}</div>
          <div className="text-xs text-foreground/70">{subtitle}</div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, idx) => (
              <Card key={idx}>
                <CardContent className="space-y-2 p-3 md:p-4">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-7 w-28" />
                  <Skeleton className="h-3 w-52" />
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="space-y-1">
          <div className="text-base font-medium">{title}</div>
          <div className="text-xs text-foreground/70">{subtitle}</div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-red-400">{error}</div>
        </CardContent>
      </Card>
    )
  }

  if (rows.length === 0) {
    return (
      <Card>
        <CardHeader className="space-y-1">
          <div className="text-base font-medium">{title}</div>
          <div className="text-xs text-foreground/70">{subtitle}</div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-foreground/70">Sem dados de planos neste mês.</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="space-y-1">
        <div className="text-base font-medium">{title}</div>
        <div className="text-xs text-foreground/70">{subtitle}</div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {rows.map((r, idx) => {
            const intervalLabel = r.plan_interval ? ` • ${String(r.plan_interval).toUpperCase()}` : ''
            const highlight = idx === 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-primary/10 text-primary'
            const notDueLabel = r.not_due_accounts_count > 0 ? ` • Não vence: ${r.not_due_accounts_count}` : ''
            return (
              <Card key={`${r.plan_id || 'none'}-${idx}`}>
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[10px] sm:text-[11px] text-foreground/70 leading-tight break-words">
                        {r.plan_name}{intervalLabel}
                      </div>
                      <div className="mt-1 text-lg sm:text-xl font-semibold text-primary">
                        {formatMoneyFromCents(r.estimated_revenue_cents, 'BRL')}
                      </div>
                    </div>
                    <div className={`rounded-lg p-1.5 sm:p-2 ${highlight}`}>
                      <Layers className="h-5 w-5" />
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-foreground/70">
                    Ativos: {r.eligible_accounts_count} • Pagos: {r.paid_accounts_count} • A vencer: {r.due_accounts_count}{notDueLabel}
                  </div>
                  <div className="mt-1 text-xs text-foreground/70">
                    Pago: {formatMoneyFromCents(r.already_paid_cents, 'BRL')} • Previsto: {formatMoneyFromCents(r.due_cents, 'BRL')}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
